import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { useAuth } from '../hooks/useAuth';
import { useDefaultExerciseSettings } from '../hooks/useDefaultExerciseSettings';
import { useNotificationSettings } from '../hooks/useNotificationSettings';
import { useTimezoneSettings } from '../hooks/useTimezoneSettings';
import { useScheduler } from '../hooks/useScheduler';
import { useLogsCleanupSettings } from '../hooks/useLogsCleanupSettings';
import { useInputFocus } from '../hooks/useInputFocus';
import Navigation from '../components/Navigation';
import { Save, ArrowLeft, Bell, Clock, Play, Square, Zap, Globe, Trash2, User, Phone, Eye, EyeOff, Lock } from 'lucide-react';
import toast from 'react-hot-toast';
import { apiService } from '../services/api';
import { TrainerUpdate } from '../types';

const SettingsPage: React.FC = () => {
  const navigate = useNavigate();
  const { trainer } = useAuth();
  const { settings, isLoading, saveSettings, isSaving } = useDefaultExerciseSettings();
  const { 
    settings: notificationSettings, 
    isLoading: isNotificationLoading, 
    saveSettings: saveNotificationSettings, 
    isSaving: isNotificationSaving 
  } = useNotificationSettings();
  const {
    settings: timezoneSettings,
    isLoading: isTimezoneLoading,
    saveSettings: saveTimezoneSettings,
    isSaving: isTimezoneSaving
  } = useTimezoneSettings();
  const { 
    status: schedulerStatus, 
    isLoading: isSchedulerLoading, 
    startScheduler, 
    stopScheduler, 
    testNotification 
  } = useScheduler();
  const { 
    settings: logsCleanupSettings, 
    isLoading: isLogsCleanupLoading, 
    saveSettings: saveLogsCleanupSettings, 
    isSaving: isLogsCleanupSaving,
    runCleanup,
    isRunningCleanup
  } = useLogsCleanupSettings();
  const handleInputFocus = useInputFocus();

  // Проверяем, является ли тренер администратором
  const isAdmin = trainer?.is_admin === true;
  
  const [formData, setFormData] = useState({
    sets: 3,
    reps: 10,
    weight: 0,
    rest_time: 90
  });

  const [notificationFormData, setNotificationFormData] = useState({
    notifications_enabled: false,
    notification_time: '19:00'
  });

  const [timezoneFormData, setTimezoneFormData] = useState({
    timezone: 'Europe/Moscow'
  });

  const [logsCleanupFormData, setLogsCleanupFormData] = useState({
    logs_cleanup_enabled: false,
    logs_cleanup_days: 30
  });

  // Состояние для формы личных данных
  const [personalData, setPersonalData] = useState({
    first_name: trainer?.first_name || '',
    last_name: trainer?.last_name || '',
    phone: trainer?.phone || ''
  });
  
  const [isPersonalDataSaving, setIsPersonalDataSaving] = useState(false);

  // Состояние для формы смены пароля
  const [passwordData, setPasswordData] = useState({
    current_password: '',
    new_password: '',
    confirm_password: ''
  });
  
  const [isPasswordSaving, setIsPasswordSaving] = useState(false);
  const [showPasswords, setShowPasswords] = useState({
    current: false,
    new: false,
    confirm: false
  });

  // Обновляем форму при загрузке настроек
  useEffect(() => {
    if (settings) {
      setFormData(settings);
    }
  }, [settings]);

  // Обновляем форму уведомлений при загрузке настроек
  useEffect(() => {
    if (notificationSettings) {
      setNotificationFormData(notificationSettings);
    }
  }, [notificationSettings]);

  // Обновляем форму часового пояса при загрузке настроек
  useEffect(() => {
    if (timezoneSettings) {
      setTimezoneFormData(timezoneSettings);
    }
  }, [timezoneSettings]);

  // Обновляем форму настроек очистки логов при загрузке настроек
  useEffect(() => {
    if (logsCleanupSettings) {
      setLogsCleanupFormData(logsCleanupSettings);
    }
  }, [logsCleanupSettings]);

  // Обновляем личные данные при изменении данных тренера
  useEffect(() => {
    if (trainer) {
      setPersonalData({
        first_name: trainer.first_name || '',
        last_name: trainer.last_name || '',
        phone: trainer.phone || ''
      });
    }
  }, [trainer]);

  const handleSave = async () => {
    try {
      await saveSettings(formData);
      toast.success('Настройки упражнений сохранены!');
    } catch (error) {
      toast.error('Ошибка при сохранении настроек упражнений');
    }
  };

  const handleNotificationSave = async () => {
    try {
      await saveNotificationSettings(notificationFormData);
      toast.success('Настройки уведомлений сохранены!');
    } catch (error) {
      toast.error('Ошибка при сохранении настроек уведомлений');
    }
  };

  const handleInputChange = (field: keyof typeof formData, value: number) => {
    setFormData(prev => ({
      ...prev,
      [field]: value
    }));
  };

  const handleNotificationChange = (field: keyof typeof notificationFormData, value: any) => {
    setNotificationFormData(prev => ({
      ...prev,
      [field]: value
    }));
  };

  const handleTimezoneChange = (field: keyof typeof timezoneFormData, value: any) => {
    setTimezoneFormData(prev => ({
      ...prev,
      [field]: value
    }));
  };

  const handleLogsCleanupChange = (field: keyof typeof logsCleanupFormData, value: any) => {
    setLogsCleanupFormData(prev => ({
      ...prev,
      [field]: value
    }));
  };

  const handleTimezoneSave = async () => {
    try {
      await saveTimezoneSettings(timezoneFormData);
      toast.success('Настройки часового пояса сохранены!');
    } catch (error) {
      toast.error('Ошибка при сохранении настроек часового пояса');
    }
  };

  const handleLogsCleanupSave = async () => {
    try {
      await saveLogsCleanupSettings(logsCleanupFormData);
    } catch (error) {
      toast.error('Ошибка при сохранении настроек очистки логов');
    }
  };

  const handleRunLogsCleanup = async () => {
    try {
      await runCleanup();
    } catch (error) {
      toast.error('Ошибка при выполнении очистки логов');
    }
  };

  const handleStartScheduler = async () => {
    try {
      await startScheduler();
      toast.success('Планировщик запущен!');
    } catch (error) {
      toast.error('Ошибка при запуске планировщика');
    }
  };

  const handleStopScheduler = async () => {
    try {
      await stopScheduler();
      toast.success('Планировщик остановлен!');
    } catch (error) {
      toast.error('Ошибка при остановке планировщика');
    }
  };

  const handleTestNotification = async () => {
    try {
      await testNotification();
      toast.success('Тестовое уведомление отправлено!');
    } catch (error) {
      toast.error('Ошибка при отправке тестового уведомления');
    }
  };

  // Функция для форматирования телефона
  const formatPhone = (value: string) => {
    // Убираем все символы кроме цифр
    const digits = value.replace(/\D/g, '');
    
    // Если номер начинается с 8, заменяем на 7
    let normalizedDigits = digits.startsWith('8') ? '7' + digits.slice(1) : digits;
    
    // Если номер не начинается с 7, добавляем 7
    if (!normalizedDigits.startsWith('7') && normalizedDigits.length > 0) {
      normalizedDigits = '7' + normalizedDigits;
    }
    
    // Ограничиваем длину до 11 цифр
    normalizedDigits = normalizedDigits.slice(0, 11);
    
    // Возвращаем номер в простом формате +7XXXXXXXXXX
    if (normalizedDigits.length === 0) return '';
    return `+${normalizedDigits}`;
  };

  const handlePersonalDataChange = (field: keyof typeof personalData, value: string) => {
    if (field === 'phone') {
      value = formatPhone(value);
    }
    setPersonalData(prev => ({
      ...prev,
      [field]: value
    }));
  };

  const handlePasswordChange = (field: keyof typeof passwordData, value: string) => {
    setPasswordData(prev => ({
      ...prev,
      [field]: value
    }));
  };

  const handlePersonalDataSave = async () => {
    try {
      setIsPersonalDataSaving(true);
      
      const updateData: TrainerUpdate = {};
      
      if (personalData.first_name !== trainer?.first_name) {
        updateData.first_name = personalData.first_name;
      }
      if (personalData.last_name !== trainer?.last_name) {
        updateData.last_name = personalData.last_name;
      }
      if (personalData.phone !== trainer?.phone) {
        updateData.phone = personalData.phone;
      }

      if (Object.keys(updateData).length === 0) {
        toast.success('Нет изменений для сохранения');
        return;
      }

      await apiService.updateProfile(updateData);
      toast.success('Личные данные обновлены!');
      
      // Обновляем контекст аутентификации
      window.location.reload(); // Простое решение для обновления данных
    } catch (error: any) {
      toast.error(error?.response?.data?.detail || 'Ошибка при обновлении личных данных');
    } finally {
      setIsPersonalDataSaving(false);
    }
  };

  const handlePasswordSave = async () => {
    try {
      setIsPasswordSaving(true);
      
      if (!passwordData.current_password) {
        toast.error('Введите текущий пароль');
        return;
      }
      
      if (!passwordData.new_password) {
        toast.error('Введите новый пароль');
        return;
      }
      
      if (passwordData.new_password.length < 6) {
        toast.error('Новый пароль должен быть не менее 6 символов');
        return;
      }
      
      if (passwordData.new_password !== passwordData.confirm_password) {
        toast.error('Пароли не совпадают');
        return;
      }

      await apiService.changePassword({
        current_password: passwordData.current_password,
        new_password: passwordData.new_password
      });
      
      toast.success('Пароль успешно изменен!');
      
      // Очищаем форму
      setPasswordData({
        current_password: '',
        new_password: '',
        confirm_password: ''
      });
    } catch (error: any) {
      toast.error(error?.response?.data?.detail || 'Ошибка при изменении пароля');
    } finally {
      setIsPasswordSaving(false);
    }
  };

  return (
    <div className="min-h-screen bg-gray-50">
      <Navigation currentPage="settings" />
      
      <main className="max-w-4xl mx-auto py-6 px-4 sm:px-6 lg:px-8">
        <div className="mb-6">
          <button
            onClick={() => navigate(-1)}
            className="flex items-center text-gray-600 hover:text-gray-900 mb-4"
          >
            <ArrowLeft size={20} className="mr-2" />
            Назад
          </button>
          
          <h1 className="text-3xl font-bold text-gray-900">Настройки</h1>
          <p className="mt-2 text-gray-600">
            Настройте параметры по умолчанию для упражнений
          </p>
        </div>

        {/* Блок личных данных */}
        <div className="bg-white shadow rounded-lg mb-6">
          <div className="px-6 py-4 border-b border-gray-200">
            <h2 className="text-lg font-medium text-gray-900 flex items-center">
              <User size={20} className="mr-2 text-blue-600" />
              Личные данные
            </h2>
            <p className="mt-1 text-sm text-gray-600">
              Изменение имени, фамилии, телефона и пароля
            </p>
          </div>

          <div className="px-6 py-6">
            {/* Форма личных данных */}
            <div className="grid grid-cols-1 md:grid-cols-2 gap-6 mb-8">
              {/* Имя */}
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-2">
                  Имя
                </label>
                <input
                  type="text"
                  value={personalData.first_name}
                  onChange={(e) => handlePersonalDataChange('first_name', e.target.value)}
                  className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-blue-500"
                  disabled={isPersonalDataSaving}
                />
              </div>

              {/* Фамилия */}
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-2">
                  Фамилия
                </label>
                <input
                  type="text"
                  value={personalData.last_name}
                  onChange={(e) => handlePersonalDataChange('last_name', e.target.value)}
                  className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-blue-500"
                  disabled={isPersonalDataSaving}
                />
              </div>

              {/* Телефон */}
              <div className="md:col-span-2">
                <label className="block text-sm font-medium text-gray-700 mb-2 flex items-center">
                  <Phone size={16} className="mr-2 text-gray-500" />
                  Телефон
                </label>
                <input
                  type="tel"
                  value={personalData.phone}
                  onChange={(e) => handlePersonalDataChange('phone', e.target.value)}
                  placeholder="+79001234567"
                  className="w-full md:w-1/2 px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-blue-500"
                  disabled={isPersonalDataSaving}
                />
              </div>
            </div>

            <div className="flex justify-end mb-8">
              <button
                onClick={handlePersonalDataSave}
                disabled={isPersonalDataSaving}
                className="flex items-center px-4 py-2 bg-blue-600 text-white rounded-md hover:bg-blue-700 focus:outline-none focus:ring-2 focus:ring-blue-500 focus:ring-offset-2 disabled:opacity-50 disabled:cursor-not-allowed"
              >
                <Save size={16} className="mr-2" />
                {isPersonalDataSaving ? 'Сохранение...' : 'Сохранить личные данные'}
              </button>
            </div>

            {/* Форма изменения пароля */}
            <div className="border-t border-gray-200 pt-8">
              <h3 className="text-md font-medium text-gray-900 flex items-center mb-6">
                <Lock size={18} className="mr-2 text-gray-600" />
                Изменение пароля
              </h3>

              <div className="grid grid-cols-1 md:grid-cols-3 gap-6">
                {/* Текущий пароль */}
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-2">
                    Текущий пароль
                  </label>
                  <div className="relative">
                    <input
                      type={showPasswords.current ? 'text' : 'password'}
                      value={passwordData.current_password}
                      onChange={(e) => handlePasswordChange('current_password', e.target.value)}
                      className="w-full px-3 py-2 pr-10 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-blue-500"
                      disabled={isPasswordSaving}
                    />
                    <button
                      type="button"
                      onClick={() => setShowPasswords(prev => ({ ...prev, current: !prev.current }))}
                      className="absolute inset-y-0 right-0 flex items-center pr-3"
                    >
                      {showPasswords.current ? (
                        <EyeOff size={16} className="text-gray-400" />
                      ) : (
                        <Eye size={16} className="text-gray-400" />
                      )}
                    </button>
                  </div>
                </div>

                {/* Новый пароль */}
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-2">
                    Новый пароль
                  </label>
                  <div className="relative">
                    <input
                      type={showPasswords.new ? 'text' : 'password'}
                      value={passwordData.new_password}
                      onChange={(e) => handlePasswordChange('new_password', e.target.value)}
                      className="w-full px-3 py-2 pr-10 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-blue-500"
                      disabled={isPasswordSaving}
                    />
                    <button
                      type="button"
                      onClick={() => setShowPasswords(prev => ({ ...prev, new: !prev.new }))}
                      className="absolute inset-y-0 right-0 flex items-center pr-3"
                    >
                      {showPasswords.new ? (
                        <EyeOff size={16} className="text-gray-400" />
                      ) : (
                        <Eye size={16} className="text-gray-400" />
                      )}
                    </button>
                  </div>
                </div>

                {/* Подтверждение пароля */}
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-2">
                    Подтвердите пароль
                  </label>
                  <div className="relative">
                    <input
                      type={showPasswords.confirm ? 'text' : 'password'}
                      value={passwordData.confirm_password}
                      onChange={(e) => handlePasswordChange('confirm_password', e.target.value)}
                      className="w-full px-3 py-2 pr-10 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-blue-500"
                      disabled={isPasswordSaving}
                    />
                    <button
                      type="button"
                      onClick={() => setShowPasswords(prev => ({ ...prev, confirm: !prev.confirm }))}
                      className="absolute inset-y-0 right-0 flex items-center pr-3"
                    >
                      {showPasswords.confirm ? (
                        <EyeOff size={16} className="text-gray-400" />
                      ) : (
                        <Eye size={16} className="text-gray-400" />
                      )}
                    </button>
                  </div>
                </div>
              </div>

              <div className="flex justify-end mt-6">
                <button
                  onClick={handlePasswordSave}
                  disabled={isPasswordSaving}
                  className="flex items-center px-4 py-2 bg-red-600 text-white rounded-md hover:bg-red-700 focus:outline-none focus:ring-2 focus:ring-red-500 focus:ring-offset-2 disabled:opacity-50 disabled:cursor-not-allowed"
                >
                  <Lock size={16} className="mr-2" />
                  {isPasswordSaving ? 'Изменение...' : 'Изменить пароль'}
                </button>
              </div>
            </div>
          </div>
        </div>

        <div className="bg-white shadow rounded-lg">
          <div className="px-6 py-4 border-b border-gray-200">
            <h2 className="text-lg font-medium text-gray-900">
              Настройки упражнений по умолчанию
            </h2>
            <p className="mt-1 text-sm text-gray-600">
              Эти настройки будут применяться при добавлении новых упражнений к тренировкам
            </p>
          </div>

          <div className="px-6 py-6 space-y-6">
            {/* Подходы */}
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-2">
                Количество подходов
              </label>
                              <input
                  type="number"
                  min="1"
                  max="20"
                  value={formData.sets}
                  onChange={(e) => handleInputChange('sets', parseInt(e.target.value) || 1)}
                  onFocus={handleInputFocus}
                  className="w-32 px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-blue-500"
                  disabled={isLoading}
                />
            </div>

            {/* Повторения */}
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-2">
                Количество повторений
              </label>
                              <input
                  type="number"
                  min="1"
                  max="100"
                  value={formData.reps}
                  onChange={(e) => handleInputChange('reps', parseInt(e.target.value) || 1)}
                  onFocus={handleInputFocus}
                  className="w-32 px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-blue-500"
                  disabled={isLoading}
                />
            </div>

            {/* Вес по умолчанию */}
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-2">
                Вес по умолчанию (кг)
              </label>
                              <input
                  type="number"
                  min="0"
                  step="0.5"
                  value={formData.weight}
                  onChange={(e) => handleInputChange('weight', parseFloat(e.target.value) || 0)}
                  onFocus={handleInputFocus}
                  className="w-32 px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-blue-500"
                  disabled={isLoading}
                />
              <p className="mt-1 text-sm text-gray-500">
                Используется как 2-й приоритет, если в истории тренировок нет данных о весе
              </p>
            </div>

            {/* Время отдыха */}
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-2">
                Время отдыха между подходами
              </label>
              <div className="flex items-center space-x-4">
                <input
                  type="number"
                  min="0"
                  max="600"
                  value={formData.rest_time}
                  onChange={(e) => handleInputChange('rest_time', parseInt(e.target.value) || 0)}
                  onFocus={handleInputFocus}
                  className="w-32 px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-blue-500"
                  disabled={isLoading}
                />
                <span className="text-sm text-gray-600">
                  {Math.floor(formData.rest_time / 60)}:{(formData.rest_time % 60).toString().padStart(2, '0')}
                </span>
              </div>
            </div>
          </div>

          <div className="px-6 py-4 bg-gray-50 border-t border-gray-200 flex justify-end">
            <button
              onClick={handleSave}
              disabled={isLoading || isSaving}
              className="flex items-center px-4 py-2 bg-blue-600 text-white rounded-md hover:bg-blue-700 focus:outline-none focus:ring-2 focus:ring-blue-500 focus:ring-offset-2 disabled:opacity-50 disabled:cursor-not-allowed"
            >
              <Save size={16} className="mr-2" />
              {isSaving ? 'Сохранение...' : 'Сохранить настройки упражнений'}
            </button>
          </div>
        </div>

        {/* Секция настроек уведомлений */}
        <div className="bg-white shadow rounded-lg mt-6">
          <div className="px-6 py-4 border-b border-gray-200">
            <h2 className="text-lg font-medium text-gray-900 flex items-center">
              <Bell size={20} className="mr-2 text-blue-600" />
              Настройки уведомлений
            </h2>
            <p className="mt-1 text-sm text-gray-600">
              Настройте автоматические уведомления о тренировках на завтра
            </p>
          </div>

          <div className="px-6 py-6 space-y-6">
            {/* Включение уведомлений */}
            <div className="flex items-center justify-between">
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">
                  Включить уведомления
                </label>
                <p className="text-sm text-gray-500">
                  Получать уведомления о тренировках на завтра
                </p>
              </div>
              <label className="relative inline-flex items-center cursor-pointer">
                <input
                  type="checkbox"
                  checked={notificationFormData.notifications_enabled}
                  onChange={(e) => handleNotificationChange('notifications_enabled', e.target.checked)}
                  disabled={isNotificationLoading}
                  className="sr-only peer"
                />
                <div className="w-11 h-6 bg-gray-200 peer-focus:outline-none peer-focus:ring-4 peer-focus:ring-blue-300 rounded-full peer peer-checked:after:translate-x-full peer-checked:after:border-white after:content-[''] after:absolute after:top-[2px] after:left-[2px] after:bg-white after:border-gray-300 after:border after:rounded-full after:h-5 after:w-5 after:transition-all peer-checked:bg-blue-600"></div>
              </label>
            </div>

            {/* Время уведомления */}
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-2 flex items-center">
                <Clock size={16} className="mr-2 text-gray-500" />
                Время уведомления
              </label>
              <input
                type="time"
                value={notificationFormData.notification_time}
                onChange={(e) => handleNotificationChange('notification_time', e.target.value)}
                disabled={isNotificationLoading || !notificationFormData.notifications_enabled}
                className="w-32 px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-blue-500 disabled:opacity-50 disabled:cursor-not-allowed"
              />
              <p className="mt-1 text-sm text-gray-500">
                Уведомления будут отправляться каждый день в указанное время
              </p>
            </div>
          </div>

          <div className="px-6 py-4 bg-gray-50 border-t border-gray-200 flex justify-end">
            <button
              onClick={handleNotificationSave}
              disabled={isNotificationLoading || isNotificationSaving}
              className="flex items-center px-4 py-2 bg-green-600 text-white rounded-md hover:bg-green-700 focus:outline-none focus:ring-2 focus:ring-green-500 focus:ring-offset-2 disabled:opacity-50 disabled:cursor-not-allowed"
            >
                          <Save size={16} className="mr-2" />
            {isNotificationSaving ? 'Сохранение...' : 'Сохранить настройки уведомлений'}
          </button>
        </div>
      </div>

      {/* Секция настроек часового пояса */}
      <div className="bg-white shadow rounded-lg mt-6">
        <div className="px-6 py-4 border-b border-gray-200">
          <h2 className="text-lg font-medium text-gray-900 flex items-center">
            <Globe size={20} className="mr-2 text-purple-600" />
            Настройки часового пояса
          </h2>
          <p className="mt-1 text-sm text-gray-600">
            Выберите ваш часовой пояс для корректного отображения времени
          </p>
        </div>

        <div className="px-6 py-6 space-y-6">
          {/* Выбор часового пояса */}
          <div>
            <label className="block text-sm font-medium text-gray-700 mb-2">
              Часовой пояс
            </label>
            <select
              value={timezoneFormData.timezone}
              onChange={(e) => handleTimezoneChange('timezone', e.target.value)}
              disabled={isTimezoneLoading}
              className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-purple-500 focus:border-purple-500 disabled:opacity-50 disabled:cursor-not-allowed"
            >
              <option value="Europe/Moscow">Москва (UTC+3)</option>
              <option value="Europe/Kaliningrad">Калининград (UTC+2)</option>
              <option value="Europe/Samara">Самара (UTC+4)</option>
              <option value="Asia/Yekaterinburg">Екатеринбург (UTC+5)</option>
              <option value="Asia/Novosibirsk">Новосибирск (UTC+7)</option>
              <option value="Asia/Krasnoyarsk">Красноярск (UTC+7)</option>
              <option value="Asia/Irkutsk">Иркутск (UTC+8)</option>
              <option value="Asia/Yakutsk">Якутск (UTC+9)</option>
              <option value="Asia/Vladivostok">Владивосток (UTC+10)</option>
              <option value="Asia/Magadan">Магадан (UTC+11)</option>
              <option value="Asia/Kamchatka">Камчатка (UTC+12)</option>
              <option value="Europe/London">Лондон (UTC+0)</option>
              <option value="Europe/Paris">Париж (UTC+1)</option>
              <option value="Europe/Berlin">Берлин (UTC+1)</option>
              <option value="America/New_York">Нью-Йорк (UTC-5)</option>
              <option value="America/Los_Angeles">Лос-Анджелес (UTC-8)</option>
              <option value="Asia/Tokyo">Токио (UTC+9)</option>
              <option value="Asia/Shanghai">Шанхай (UTC+8)</option>
              <option value="Asia/Dubai">Дубай (UTC+4)</option>
            </select>
            <p className="mt-1 text-sm text-gray-500">
              Выберите часовой пояс вашего региона для корректного отображения времени
            </p>
          </div>
        </div>

        <div className="px-6 py-4 bg-gray-50 border-t border-gray-200 flex justify-end">
          <button
            onClick={handleTimezoneSave}
            disabled={isTimezoneLoading || isTimezoneSaving}
            className="flex items-center px-4 py-2 bg-purple-600 text-white rounded-md hover:bg-purple-700 focus:outline-none focus:ring-2 focus:ring-purple-500 focus:ring-offset-2 disabled:opacity-50 disabled:cursor-not-allowed"
          >
            <Save size={16} className="mr-2" />
            {isTimezoneSaving ? 'Сохранение...' : 'Сохранить настройки часового пояса'}
          </button>
        </div>
      </div>

      {/* Секция управления планировщиком - только для админов */}
      {isAdmin && (
        <div className="bg-white shadow rounded-lg mt-6">
          <div className="px-6 py-4 border-b border-gray-200">
            <h2 className="text-lg font-medium text-gray-900 flex items-center">
              <Zap size={20} className="mr-2 text-orange-600" />
              Управление планировщиком
            </h2>
            <p className="mt-1 text-sm text-gray-600">
              Управляйте автоматической отправкой уведомлений. Планировщик запускается автоматически при старте приложения.
            </p>
          </div>

          <div className="px-6 py-6 space-y-6">
            {/* Статус планировщика */}
            <div className="flex items-center justify-between">
              <div>
                <span className="block text-sm font-medium text-gray-700 mb-1">
                  Статус планировщика
                </span>
                <span className={`inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-medium ${
                  schedulerStatus?.is_running 
                    ? 'bg-green-100 text-green-800' 
                    : 'bg-red-100 text-red-800'
                }`}>
                  {schedulerStatus?.is_running ? 'Запущен' : 'Остановлен'}
                </span>
              </div>
              
              <div className="flex space-x-2">
                <button
                  onClick={handleStartScheduler}
                  disabled={isSchedulerLoading || schedulerStatus?.is_running}
                  className="flex items-center px-3 py-1.5 bg-green-600 text-white text-sm rounded-md hover:bg-green-700 focus:outline-none focus:ring-2 focus:ring-green-500 focus:ring-offset-2 disabled:opacity-50 disabled:cursor-not-allowed"
                >
                  <Play size={14} className="mr-1" />
                  Запустить
                </button>
                
                <button
                  onClick={handleStopScheduler}
                  disabled={isSchedulerLoading || !schedulerStatus?.is_running}
                  className="flex items-center px-3 py-1.5 bg-red-600 text-white text-sm rounded-md hover:bg-red-700 focus:outline-none focus:ring-2 focus:ring-red-500 focus:ring-offset-2 disabled:opacity-50 disabled:cursor-not-allowed"
                >
                  <Square size={14} className="mr-1" />
                  Остановить
                </button>
              </div>
            </div>

            {/* Информация о планировщике */}
            {schedulerStatus && (
              <div className="bg-gray-50 rounded-md p-4">
                <div className="grid grid-cols-2 gap-4 text-sm">
                  <div>
                    <span className="text-gray-600">Задач в расписании:</span>
                    <span className="ml-2 font-medium">{schedulerStatus.jobs_count}</span>
                  </div>
                  {schedulerStatus.next_run && (
                    <div>
                      <span className="text-gray-600">Следующий запуск:</span>
                      <span className="ml-2 font-medium">{schedulerStatus.next_run}</span>
                    </div>
                  )}
                </div>
              </div>
            )}

            {/* Тестовая отправка */}
            <div className="border-t border-gray-200 pt-4">
              <div className="flex items-center justify-between">
                <div>
                  <span className="block text-sm font-medium text-gray-700 mb-1">
                    Тестовая отправка
                  </span>
                  <span className="text-sm text-gray-500">
                    Отправить тестовое уведомление сейчас
                  </span>
                </div>
                
                <button
                  onClick={handleTestNotification}
                  disabled={isSchedulerLoading}
                  className="flex items-center px-3 py-1.5 bg-blue-600 text-white text-sm rounded-md hover:bg-blue-700 focus:outline-none focus:ring-2 focus:ring-blue-500 focus:ring-offset-2 disabled:opacity-50 disabled:cursor-not-allowed"
                >
                  <Zap size={14} className="mr-1" />
                  Отправить тест
                </button>
              </div>
            </div>
          </div>
        </div>
      )}

      {/* Секция настроек очистки логов - только для админов */}
      {isAdmin && (
        <div className="bg-white shadow rounded-lg mt-6">
          <div className="px-6 py-4 border-b border-gray-200">
            <h2 className="text-lg font-medium text-gray-900 flex items-center">
              <Trash2 size={20} className="mr-2 text-red-600" />
              Настройки очистки логов действий тренеров
            </h2>
            <p className="mt-1 text-sm text-gray-600">
              Настройте автоматическую очистку старых логов действий тренеров для освобождения места в базе данных
            </p>
          </div>

          <div className="px-6 py-6 space-y-6">
            {/* Включение очистки логов */}
            <div className="flex items-center justify-between">
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">
                  Включить очистку логов действий тренеров
                </label>
                <p className="text-sm text-gray-500">
                  Автоматически удалять старые логи действий тренеров согласно настройкам
                </p>
              </div>
              <label className="relative inline-flex items-center cursor-pointer">
                <input
                  type="checkbox"
                  checked={logsCleanupFormData.logs_cleanup_enabled}
                  onChange={(e) => handleLogsCleanupChange('logs_cleanup_enabled', e.target.checked)}
                  disabled={isLogsCleanupLoading}
                  className="sr-only peer"
                />
                <div className="w-11 h-6 bg-gray-200 peer-focus:outline-none peer-focus:ring-4 peer-focus:ring-red-300 rounded-full peer peer-checked:after:translate-x-full peer-checked:after:border-white after:content-[''] after:absolute after:top-[2px] after:left-[2px] after:bg-white after:border-gray-300 after:border after:rounded-full after:h-5 after:w-5 after:transition-all peer-checked:bg-red-600"></div>
              </label>
            </div>

            {/* Количество дней хранения */}
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-2">
                Количество дней хранения логов действий
              </label>
              <input
                type="number"
                min="1"
                max="365"
                value={logsCleanupFormData.logs_cleanup_days}
                onChange={(e) => handleLogsCleanupChange('logs_cleanup_days', parseInt(e.target.value) || 30)}
                disabled={isLogsCleanupLoading || !logsCleanupFormData.logs_cleanup_enabled}
                className="w-32 px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-red-500 focus:border-red-500 disabled:opacity-50 disabled:cursor-not-allowed"
              />
              <p className="mt-1 text-sm text-gray-500">
                Логи действий тренеров старше указанного количества дней будут автоматически удалены
              </p>
            </div>

            {/* Ручная очистка логов */}
            <div className="border-t border-gray-200 pt-4">
              <div className="flex items-center justify-between">
                <div>
                  <span className="block text-sm font-medium text-gray-700 mb-1">
                    Ручная очистка логов действий
                  </span>
                  <span className="text-sm text-gray-500">
                    Выполнить очистку логов действий тренеров сейчас согласно текущим настройкам
                  </span>
                </div>
                
                <button
                  onClick={handleRunLogsCleanup}
                  disabled={isRunningCleanup || !logsCleanupFormData.logs_cleanup_enabled}
                  className="flex items-center px-3 py-1.5 bg-red-600 text-white text-sm rounded-md hover:bg-red-700 focus:outline-none focus:ring-2 focus:ring-red-500 focus:ring-offset-2 disabled:opacity-50 disabled:cursor-not-allowed"
                >
                  <Trash2 size={14} className="mr-1" />
                  {isRunningCleanup ? 'Очистка...' : 'Очистить логи действий'}
                </button>
              </div>
            </div>
          </div>

          <div className="px-6 py-4 bg-gray-50 border-t border-gray-200 flex justify-end">
            <button
              onClick={handleLogsCleanupSave}
              disabled={isLogsCleanupLoading || isLogsCleanupSaving}
              className="flex items-center px-4 py-2 bg-red-600 text-white rounded-md hover:bg-red-700 focus:outline-none focus:ring-2 focus:ring-red-500 focus:ring-offset-2 disabled:opacity-50 disabled:cursor-not-allowed"
            >
              <Save size={16} className="mr-2" />
              {isLogsCleanupSaving ? 'Сохранение...' : 'Сохранить настройки очистки логов действий'}
            </button>
          </div>
        </div>
      )}
    </main>
  </div>
);
};

export default SettingsPage; 